import fs from "node:fs"
import path from "node:path"
import { globSync } from "glob"

type WorkspacePackageJson = {
  workspaces?: string[] | { packages?: string[] }
}

type LernaJson = {
  packages?: string[]
}

export const sourceEntryFileNames = [
  "index.ts",
  "index.tsx",
  "index.mts",
  "index.cts",
  "index.js",
  "index.jsx",
  "index.mjs",
  "index.cjs",
]

function stripInlineYamlComment(line: string): string {
  let quote: "'" | "\"" | null = null

  for (let index = 0; index < line.length; index += 1) {
    const char = line[index]
    const previous = index > 0 ? line[index - 1] : ""

    if ((char === "'" || char === "\"") && previous !== "\\") {
      quote = quote === char ? null : quote ?? char
      continue
    }

    if (char === "#" && !quote && (index === 0 || /\s/.test(previous))) {
      return line.slice(0, index)
    }
  }

  return line
}

function unquoteYamlScalar(value: string): string {
  const trimmed = value.trim()
  const quote = trimmed[0]

  if ((quote === "'" || quote === "\"") && trimmed.endsWith(quote)) {
    return trimmed.slice(1, -1)
  }

  return trimmed
}

function splitInlineYamlArray(value: string): string[] {
  const trimmed = value.trim()
  if (!trimmed.startsWith("[") || !trimmed.endsWith("]")) {
    return []
  }

  const items: string[] = []
  let current = ""
  let quote: "'" | "\"" | null = null

  for (const char of trimmed.slice(1, -1)) {
    if (char === "'" || char === "\"") {
      quote = quote === char ? null : quote ?? char
      current += char
      continue
    }

    if (char === "," && !quote) {
      items.push(current)
      current = ""
      continue
    }

    current += char
  }

  items.push(current)
  return items.map(item => unquoteYamlScalar(item)).filter(Boolean)
}

function parsePnpmWorkspacePackagePatterns(contents: string): string[] {
  const patterns: string[] = []
  let packagesIndent: number | null = null

  for (const rawLine of contents.split(/\r?\n/)) {
    const line = stripInlineYamlComment(rawLine)
    const trimmed = line.trim()
    if (!trimmed) {
      continue
    }

    const indent = line.length - line.trimStart().length

    if (packagesIndent === null) {
      const match = trimmed.match(/^packages\s*:\s*(.*)$/)
      if (!match) {
        continue
      }

      const inlineValue = match[1].trim()
      if (inlineValue) {
        patterns.push(...splitInlineYamlArray(inlineValue))
        continue
      }

      packagesIndent = indent
      continue
    }

    if (indent <= packagesIndent && !trimmed.startsWith("-")) {
      packagesIndent = null
      continue
    }

    if (!trimmed.startsWith("-")) {
      continue
    }

    const value = unquoteYamlScalar(trimmed.slice(1).trim())
    if (value) {
      patterns.push(value)
    }
  }

  return patterns
}

function getPackageJsonWorkspacePatterns(packageJson: WorkspacePackageJson): string[] {
  const raw = packageJson.workspaces
  if (!raw) {
    return []
  }

  return Array.isArray(raw) ? raw : (raw.packages ?? [])
}

export function readWorkspacePatterns(rootDir: string): string[] {
  const patterns: string[] = []
  const rootPackageJsonPath = path.join(rootDir, "package.json")
  if (fs.existsSync(rootPackageJsonPath)) {
    try {
      const rootPackageJson = JSON.parse(fs.readFileSync(rootPackageJsonPath, "utf8")) as WorkspacePackageJson
      patterns.push(...getPackageJsonWorkspacePatterns(rootPackageJson))
    } catch {
      // Ignore malformed package metadata and fall back to other discovery strategies.
    }
  }

  const pnpmWorkspacePath = path.join(rootDir, "pnpm-workspace.yaml")
  if (fs.existsSync(pnpmWorkspacePath)) {
    patterns.push(...parsePnpmWorkspacePackagePatterns(fs.readFileSync(pnpmWorkspacePath, "utf8")))
  }

  const lernaJsonPath = path.join(rootDir, "lerna.json")
  if (fs.existsSync(lernaJsonPath)) {
    try {
      const lernaJson = JSON.parse(fs.readFileSync(lernaJsonPath, "utf8")) as LernaJson
      patterns.push(...(lernaJson.packages ?? ["packages/*"]))
    } catch {
      // Ignore malformed Lerna metadata and fall back to other discovery strategies.
    }
  }

  return [...new Set(patterns.map(pattern => pattern.trim()).filter(Boolean))]
}

function normalizeWorkspacePattern(pattern: string): string {
  return pattern.replaceAll("\\", "/").replace(/^!/, "").replace(/\/$/, "")
}

function workspacePatternToPackageJsonGlob(pattern: string): string {
  return `${normalizeWorkspacePattern(pattern)}/package.json`
}

export function hasSourceEntry(packageDir: string): boolean {
  return sourceEntryFileNames.some(entryFileName => fs.existsSync(path.join(packageDir, "src", entryFileName)))
}

export function resolveWorkspacePackageDirs(rootDir: string): string[] {
  const packageDirs: string[] = []
  const workspacePatterns = readWorkspacePatterns(rootDir)
  const ignoredPackageJsonFiles = workspacePatterns
    .filter(pattern => pattern.startsWith("!"))
    .map(pattern => workspacePatternToPackageJsonGlob(pattern))

  for (const pattern of workspacePatterns) {
    if (pattern.startsWith("!")) {
      continue
    }

    const packageJsonFiles = globSync(workspacePatternToPackageJsonGlob(pattern), {
      absolute: true,
      cwd: rootDir,
      ignore: ["**/node_modules/**", ...ignoredPackageJsonFiles],
      nodir: true,
    })

    packageDirs.push(...packageJsonFiles.map(packageJsonPath => path.dirname(packageJsonPath)))
  }

  return [...new Set(packageDirs)].sort((a, b) => a.localeCompare(b))
}
